import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import ExcelJS from "exceljs";
import AlertManager from "../../shared/Alert/Alert";
import { getDCCostManualPullUserUploads } from "../../API/dcCostManualPullUserUploadsApi";
import { uploadDCCostManualPullFile } from "../../API/dcCostManualPullUploadApi";
import { addAlphabetOnlyValidation } from "../../API/marginRuleApi";
import {
  ACCEPTED_MIME_TYPES_WITH_CSV,
  FILE_ACCEPTED_TYPES_WITH_CSV,
  MAX_FILE_SIZE_MB,
} from "../LandedCostUpload/LandedCostUploadConstants";
import DCCostBatchDetail from "./DCCostBatchDetail";
import classes from "../LandedCostUpload/LandedCostUpload.module.css";

export const MANUAL_PULL_TEMPLATE_HEADERS = ["SKU", "HQ_LINE", "FIELD_LINE"];

const MANUAL_PULL_FROZEN_COLUMNS = [
  { field: "parsedSku", headerName: "parsedSku", width: 120 },
  { field: "batchId", headerName: "batchId", width: 110 },
  { field: "hqLine", headerName: "hqLine", width: 90 },
];

const MANUAL_PULL_COLUMN_ALIASES = {
  SKU: ["SKU", "sku", "parsedSku", "parsed_sku"],
  HQ_LINE: ["HQ_LINE", "hqLine", "hq_line"],
  FIELD_LINE: ["FIELD_LINE", "fieldLine", "field_line"],
};

const extractManualPullRows = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.rows)) {
    return payload.rows;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.content)) {
    return payload.content;
  }

  return [];
};

const extractFirstBatchId = (value, seen = new Set()) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value !== "object") {
    return null;
  }

  if (seen.has(value)) {
    return null;
  }

  seen.add(value);

  const directBatchId = value.batchId ?? value.batchID ?? value.batch_id ?? value.batchid;
  if (directBatchId !== undefined && directBatchId !== null && directBatchId !== "") {
    return directBatchId;
  }

  for (const nestedValue of Object.values(value)) {
    const nestedBatchId = extractFirstBatchId(nestedValue, seen);
    if (nestedBatchId !== null) {
      return nestedBatchId;
    }
  }

  return null;
};

const extractManualPullBatchId = (payload) => {
  const batchIdCandidates = [
    payload?.manualPullBatchId,
    payload?.manual_pull_batch_id,
    payload?.id,
    payload?.rows?.[0],
    payload?.data?.[0],
    payload?.content?.[0],
    payload?.rows,
    payload?.data,
    payload?.content,
    payload,
  ];

  for (const candidate of batchIdCandidates) {
    const batchId = extractFirstBatchId(candidate);
    if (batchId !== null && batchId !== undefined && batchId !== "") {
      return batchId;
    }
  }

  return null;
};

export const normalizeManualPullResponseRow = (row = {}, index = 0) => ({
  id: row.id || row.detailId || row.rowId || `manual-pull-row-${index}`,
  ...row,
  sku: MANUAL_PULL_COLUMN_ALIASES.SKU.map((fieldName) => row?.[fieldName]).find((value) => value !== undefined && value !== null && value !== "") || "",
  parsedSku: MANUAL_PULL_COLUMN_ALIASES.SKU.map((fieldName) => row?.[fieldName]).find((value) => value !== undefined && value !== null && value !== "") || "",
  hqLine: MANUAL_PULL_COLUMN_ALIASES.HQ_LINE.map((fieldName) => row?.[fieldName]).find((value) => value !== undefined && value !== null && value !== "") || "",
  fieldLine: MANUAL_PULL_COLUMN_ALIASES.FIELD_LINE.map((fieldName) => row?.[fieldName]).find((value) => value !== undefined && value !== null && value !== "") || "",
  SKU: MANUAL_PULL_COLUMN_ALIASES.SKU.map((fieldName) => row?.[fieldName]).find((value) => value !== undefined && value !== null && value !== "") || "",
  HQ_LINE: MANUAL_PULL_COLUMN_ALIASES.HQ_LINE.map((fieldName) => row?.[fieldName]).find((value) => value !== undefined && value !== null && value !== "") || "",
  FIELD_LINE: MANUAL_PULL_COLUMN_ALIASES.FIELD_LINE.map((fieldName) => row?.[fieldName]).find((value) => value !== undefined && value !== null && value !== "") || "",
});

const normalizeManualPullRows = (rows = [], batchId = null) =>
  rows.map((row, index) => ({
    ...normalizeManualPullResponseRow(row, index),
    batchId: row?.batchId ?? row?.batchID ?? row?.batch_id ?? row?.batchid ?? batchId ?? "",
  }));

export const buildManualPullTemplateWorkbook = (workbook = new ExcelJS.Workbook()) => {
  const worksheet = workbook.addWorksheet("Manual Pull");
  const headerRow = worksheet.addRow(MANUAL_PULL_TEMPLATE_HEADERS);

  worksheet.getColumn(1).numFmt = "@";
  addAlphabetOnlyValidation(worksheet, "B", "HQ line");

  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: "center" };
  });

  return workbook;
};

export const downloadManualPullTemplate = async () => {
  const workbook = buildManualPullTemplateWorkbook();

  const buffer = await workbook.xlsx.writeBuffer();
  const downloadUrl = window.URL.createObjectURL(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })
  );
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = "manual-pull-template.xlsx";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

const DCManualPull = () => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [loadingManualPullRows, setLoadingManualPullRows] = useState(false);
  const [uploadResponseRows, setUploadResponseRows] = useState([]);
  const [uploadBatchId, setUploadBatchId] = useState(null);

  const loadManualPullRows = useCallback(async () => {
    setLoadingManualPullRows(true);

    try {
      const payload = await getDCCostManualPullUserUploads();
      const nextBatchId = extractManualPullBatchId(payload);
      setUploadResponseRows(normalizeManualPullRows(extractManualPullRows(payload), nextBatchId));
      setUploadBatchId(nextBatchId);
    } finally {
      setLoadingManualPullRows(false);
    }
  }, []);

  useEffect(() => {
    void loadManualPullRows();
  }, [loadManualPullRows]);

  const resetFileInput = useCallback(() => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleFileChange = useCallback((event) => {
    const nextFile = event.target.files?.[0] || null;
    setSelectedFile(nextFile);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      AlertManager.error("Please choose a file before uploading.", { contactSupport: false });
      return;
    }

    const isSupportedFile =
      ACCEPTED_MIME_TYPES_WITH_CSV.includes(selectedFile.type) || /\.(xls[x]?|csv)$/i.test(selectedFile.name);

    if (!isSupportedFile) {
      AlertManager.error("Please select file with extension .xlsx, .xls, or .csv", { contactSupport: false });
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      AlertManager.error(`File size exceeds ${MAX_FILE_SIZE_MB} MB.`, { contactSupport: false });
      return;
    }

    setUploadingFile(true);

    try {
      const response = await uploadDCCostManualPullFile({ file: selectedFile });
      if (response?.ok) {
        const payload = await response.json().catch(() => null);
        const nextBatchId = extractManualPullBatchId(payload);
        setUploadResponseRows(normalizeManualPullRows(extractManualPullRows(payload), nextBatchId));
        setUploadBatchId(nextBatchId);
        AlertManager.success(`Manual pull file ${selectedFile.name} uploaded.`, { contactSupport: false });
        resetFileInput();
      }
    } finally {
      setUploadingFile(false);
    }
  }, [resetFileInput, selectedFile]);

  const handleTemplateDownload = useCallback(async () => {
    if (downloadingTemplate) {
      return;
    }

    try {
      setDownloadingTemplate(true);
      await downloadManualPullTemplate();
    } catch (error) {
      AlertManager.error("Unable to download the manual pull template.", { contactSupport: false });
    } finally {
      setDownloadingTemplate(false);
    }
  }, [downloadingTemplate]);

  return (
    <section className={classes.body}>
      <Typography variant="h4" component="h1" className={classes.heading}>
        Upload manual pull file
      </Typography>
      <form className={classes.container}>
        <Box className={classes.fieldRow}>
          <Button component="label" variant="outlined" size="small">
            Choose File
            <input hidden ref={fileInputRef} type="file" accept={FILE_ACCEPTED_TYPES_WITH_CSV} onChange={handleFileChange} />
          </Button>
          <Typography variant="body2" className={classes.fileNameDisplay}>
            {selectedFile?.name || "No file selected"}
          </Typography>
          <Typography variant="body2" className={classes.helperText}>
            Accepted formats: .xlsx / .xls / .csv · Max size {MAX_FILE_SIZE_MB} MB
          </Typography>
        </Box>
        <Box className={classes.actions}>
          <Button className={classes.uploadBtn} type="button" onClick={handleUpload} disabled={uploadingFile}>
            {uploadingFile ? "Uploading..." : "Upload"}
          </Button>
          <Tooltip title="Download the standardized manual pull template">
            <Button className={classes.uploadBtn} variant="text" type="button" onClick={handleTemplateDownload} disabled={downloadingTemplate}>
              {downloadingTemplate ? "Downloading..." : "Download Template"}
            </Button>
          </Tooltip>
        </Box>
      </form>

      <div className={classes.hLine} />

      <Box sx={{ mt: 1 }}>
        {loadingManualPullRows ? (
          <Typography variant="body2" sx={{ color: "#4b5563", mb: 1 }}>
            Loading manual pull data...
          </Typography>
        ) : null}
        <DCCostBatchDetail
          {...(uploadBatchId ? { batchIdOverride: uploadBatchId } : {})}
          exportFileNameOverride="manual pull export.xlsx"
          frozenColumnsOverride={MANUAL_PULL_FROZEN_COLUMNS}
          detailRowsOverride={uploadResponseRows}
          onDataRefresh={loadManualPullRows}
          itemLevelActionsDisabled
          hideHeader
          hideDescription
          hideEmptyStateMessage
        />
      </Box>
    </section>
  );
};

export default DCManualPull;
